import { cn, parseIdentifier } from '~/utils'
import {
  Globe,
  User,
  PanelTop,
  SquareStack,
  Workflow,
  MessagesSquare,
  MessageSquareMore,
  Zap,
  SquareTerminal,
  FileText,
  TriangleAlert,
  CircleDot,
} from 'lucide-react'
import { FC, useMemo, type ComponentProps, type ReactNode } from 'react'
import { Color } from '../types'
import { IdentifierDropdownMenu } from '~/components/IdentifierDropdownMenu'
import { Button } from '@bpinternal/ui-kit-next'

type Props = {
  id: string
  className?: string
  prefix?: ReturnType<typeof parseIdentifier>
  color?: Color
  path?: string
  studioUrl?: string
  fallback?: ReactNode
  truncate?: boolean
} & Pick<ComponentProps<typeof IdentifierDropdownMenu>, 'logsStartDate'>

const colorClasses = {
  gray: {
    button: 'hover:bg-gray-2',
    icon: 'text-gray-11',
    text: 'text-gray-11',
    decoration: 'decoration-gray-5 hover:decoration-gray-8',
  },
  indigo: {
    button: 'hover:bg-indigo-2',
    icon: 'text-indigo-11',
    text: 'text-indigo-11',
    decoration: 'decoration-indigo-5 hover:decoration-indigo-8',
  },
  blue: {
    button: 'hover:bg-accent-2',
    icon: 'text-accent-11',
    text: 'text-accent-11',
    decoration: 'decoration-accent-5 hover:decoration-accent-8',
  },
  green: {
    button: 'hover:bg-green-2',
    icon: 'text-green-11',
    text: 'text-green-11',
    decoration: 'decoration-green-5 hover:decoration-green-8',
  },
}

export const Identifier = ({
  className,
  id,
  prefix: providedPrefix,
  color = 'gray',
  path,
  fallback,
  studioUrl,
  logsStartDate,
  truncate,
  ...props
}: Props) => {
  const prefix = providedPrefix ?? parseIdentifier(id)

  const prefixIconMap: Partial<Record<ReturnType<typeof parseIdentifier>, FC<{ className?: string }>>> = useMemo(() => {
    return {
      conv: MessagesSquare,
      msg: MessageSquareMore,
      evt: Zap,
      user: User,
      url: Globe,
      wf: Workflow,
      nd: PanelTop,
      ins: SquareStack,
      issevt: CircleDot,
      iss: TriangleAlert,
      file: FileText,
    }
  }, [id])

  const Icon = prefixIconMap[prefix] ?? SquareTerminal

  if (prefix === 'unknown') {
    return <>{fallback ?? id}</>
  }

  const colorScheme = colorClasses[color as keyof typeof colorClasses] || colorClasses.gray

  return (
    <IdentifierDropdownMenu
      path={path}
      value={id}
      prefix={prefix}
      studioUrl={studioUrl}
      logsStartDate={logsStartDate}
      onClick={(e) => e.stopPropagation()}
    >
      <Button
        {...props}
        variant="ghost"
        className={cn(
          '-mx-1 h-auto max-w-full cursor-pointer gap-1 rounded-sm p-1 [&_svg]:!size-3.5',
          colorScheme.button,
          className
        )}
      >
        <Icon className={cn(colorScheme.icon)} />
        <span
          className={cn(
            'font-mono text-xs underline decoration-dashed',
            colorScheme.text,
            colorScheme.decoration,
            truncate ? 'truncate' : ''
          )}
        >
          {id}
        </span>
      </Button>
    </IdentifierDropdownMenu>
  )
}
